import { RefObject, useCallback, useEffect, useState } from 'react';
import { getPixelRatio, isNumber } from '../util';
import { useDocReader, DocReaderState } from '../store/docReaderContext';
import { lang } from '@umu/ukit-lang';
import { ImageLoadQueue } from './imageLoadQueue';
import { configRenderHelper } from './configRenderHelper';
import { RENDER_HIT_TYPE, RENDER_TYPE } from '../base';
import { renderSvg } from '../pdfRenderService';
import { ResourceStatus } from '@umu/umu-types';
import { $window } from '@umu/umu-dom';
import { getViewport } from './utils';
import debounce from 'lodash/debounce';

import { Toast } from 'common/src/services';
let pdfImagesCache: { [name: string]: string } = {};
let imageLoadQueue: ImageLoadQueue | null;
let lastPageNum: number = 0; // 记录上一次的页码，用于判断预加载的方向

interface IRefs {
    svgContainerRef: RefObject<HTMLDivElement>;
    canvasRef: RefObject<HTMLCanvasElement>;
    pageNum: number;
}
interface IOptions {
    renderType?: DocReaderState['renderType'];
    once?: boolean; // 不需要重复渲染
}

export default function useUpdateDocReaderRenderer(refs: IRefs, options?: IOptions): boolean {
    const [state, setState] = useDocReader();
    const { once } = options || {};
    const { pdfContentRef, docReaderProps, reRender, loading } = state;
    const { svgContainerRef, canvasRef, pageNum } = refs;
    const renderType = options?.renderType || state.renderType;
    const [isCurrentPageRendered, setIsCurrentPageRendered] = useState(false);

    /**
     * 暂时使用canvas画图片，后续优化
     * 主要是为了解决图片的viewport的宽高，以及scale的计算
     * @param page
     * @returns
     */
    const renderByImage = useCallback(
        (page: any, resetPosition?: boolean) => {
            const { pdfImages } = docReaderProps;
            if (!pdfImages || pageNum > pdfImages.list.length) {
                return;
            }
            if (!pdfImages.list[pageNum - 1]) {
                return;
            }

            /**
             * 视图发生变化时，需要重置图片缓存
             */
            if (resetPosition) {
                pdfImagesCache = {};
            }

            // 因为像素密度的原因会导致高密度像素的屏幕上展示时会不清晰，所以这里canvas的大小 * 像素密度，但是css为正常大小
            const canvas = canvasRef.current;
            if (!canvas) {
                return;
            }
            const context = canvas.getContext('2d');
            if (!context) {
                return;
            }

            // 再重新渲染画布前先清掉之前的画布
            const pixelRatio = getPixelRatio();
            context.clearRect(0, 0, canvas.width, canvas.height);
            // 这里引入的CSS变量是 useUpdateDocReaderScale 中设置的, 用于控制缩放, 这里使用css变量的形式是为了避免缩放的时候重复渲染
            const scale_factor = Number(getComputedStyle(canvas).getPropertyValue('--scale-factor'));
            const viewport = getViewport(page, scale_factor * pixelRatio);
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            canvas.style.width = `calc(var(--scale-factor) * ${Math.floor(
                viewport.width / (pixelRatio * scale_factor),
            )}px)`;
            canvas.style.height = `calc(var(--scale-factor) * ${Math.floor(
                viewport.height / (pixelRatio * scale_factor),
            )}px)`;

            const imageKey = pdfImages.list[pageNum - 1];
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                context.clearRect(0, 0, canvas.width, canvas.height);
                context.drawImage(img, 0, 0, viewport.width, viewport.height);
                onRenderEnd();
                setTimeout(() => {
                    const dataURL = canvas.toDataURL('image/jpg');
                    pdfImagesCache[imageKey] = dataURL;
                });
            };
            img.onerror = () => {
                Toast.show({
                    message: JSON.stringify(lang('该内容已删除')),
                    duration: 1000,
                });
                setState({
                    reRender: true,
                    isError: true,
                });
                throw new Error(
                    `pdf image load error, page index: ${pageNum}, image name: ${imageKey}, pdf url: ${docReaderProps.resourceUrl}, element id: ${docReaderProps.elementId}`,
                );
            };
            img.src = pdfImagesCache[imageKey] || `${pdfImages.path}${pdfImages.list[pageNum - 1]}`;
            imageLoadQueue?.enqueue(pageNum, lastPageNum < pageNum ? 'next' : 'pre');
        },
        [canvasRef, docReaderProps.pdfImages, pageNum],
    );

    // 缩放时清空图片缓存
    useEffect(() => {
        pdfImagesCache = {};
    }, [state.scale]);

    const renderBySvg = useCallback(
        (page: any) => {
            const viewport = page.getViewport({ scale: 1 });
            const svgContainer = svgContainerRef.current;
            const isMustUseSvg = configRenderHelper.hitType !== RENDER_HIT_TYPE.ENTERPRISE_ID;
            renderSvg(window.pdfjsLib, page, viewport, svgContainer, isMustUseSvg, (isRendered: boolean) => {
                if (isRendered) {
                    onRenderEnd();
                } else {
                    // 企业id配置的情况下会使用重试机制
                    if (!isMustUseSvg) {
                        retryCanvasRender();
                    }
                }
            });
        },
        [state.canvasRef, state.svgContainerRef],
    );
    /**
     * @desc 自动定位
     * @param {*} canvasWidth
     */
    const autoView = useCallback(
        (canvasWidth: number) => {
            const pdfContentDom = pdfContentRef.current;
            const { scale, lastScale, initScale } = state;
            if (!pdfContentDom || scale <= initScale) {
                return;
            }
            // 定位到视图的中心位置
            const offset = (canvasWidth * (scale - lastScale)) / 2;
            pdfContentDom.scrollLeft = pdfContentDom.scrollLeft + offset;
        },
        [pdfContentRef, state.scale, state.lastScale, state.initScale],
    );
    const renderByCanvas = useCallback((page: any, resetPosition?: boolean) => {
        // 因为像素密度的原因会导致高密度像素的屏幕上展示时会不清晰，所以这里canvas的大小 * 像素密度，但是css为正常大小
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }

        // 再重新渲染画布前先清掉之前的画布
        const pixelRatio = getPixelRatio();
        context.clearRect(0, 0, canvas.width, canvas.height);
        // 这里引入的CSS变量是 useUpdateDocReaderScale 中设置的, 用于控制缩放, 这里使用css变量的形式是为了避免缩放的时候重复渲染
        const scale_factor = Number(getComputedStyle(canvas).getPropertyValue('--scale-factor'));
        const viewport = getViewport(page, scale_factor * pixelRatio);
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `calc(var(--scale-factor) * ${Math.floor(
            viewport.width / (pixelRatio * scale_factor),
        )}px)`;
        canvas.style.height = `calc(var(--scale-factor) * ${Math.floor(
            viewport.height / (pixelRatio * scale_factor),
        )}px)`;
        const renderTask = page.render({
            canvasContext: context,
            viewport,
        });
        if (resetPosition) {
            autoView(canvas.clientWidth);
        }
        renderTask.promise
            .then(() => {
                onRenderEnd();
            })
            .catch((err: any) => {
                onRenderEnd();
                console.error(err);
            });
    }, []);

    /**
     * @desc 渲染pdf
     * @param {boolean} resetPosition 是否要重置视图位置
     */
    const renderPage = useCallback(
        debounce((resetPosition?: boolean) => {
            const { pdf } = state;
            if (!isNumber(pageNum) || !pdf.getPage) {
                return;
            }
            setIsCurrentPageRendered(false);

            pdf.getPage(pageNum).then((page: any) => {
                try {
                    if (renderType === RENDER_TYPE.IMAGE) {
                        renderByImage(page, resetPosition);
                    } else if (renderType === RENDER_TYPE.SVG) {
                        renderBySvg(page);
                    } else {
                        renderByCanvas(page, resetPosition);
                    }
                } catch (e) {
                    console.error(e);
                    setIsCurrentPageRendered(true);
                }
            });
        }, 200),
        [state.pdf, renderByImage, renderBySvg, renderByCanvas, renderType],
    );

    useEffect(() => {
        if (loading) {
            return;
        }
        renderPage(true);
    }, [pageNum, state.pdf, loading, refs.canvasRef, refs.svgContainerRef]);
    useEffect(() => {
        if (loading || !reRender || once) {
            return;
        }
        renderPage();
    }, [reRender]);
    useEffect(() => {
        if (loading) {
            return;
        }
        renderPage();
    }, [state.renderType]);
    const onRenderEnd = useCallback(() => {
        setIsCurrentPageRendered(true);
        if (state.isRendered && !state.reRender) {
            return;
        }
        setState({
            isRendered: true,
            reRender: false,
        });
    }, [state.isRendered, state.reRender]);
    const retryCanvasRender = useCallback(() => {
        // svg渲染失败后使用canvas渲染
        setState({
            renderType: RENDER_TYPE.CANVAS,
        });
    }, []);
    const initImage = useCallback(() => {
        imageLoadQueue?.preload(docReaderProps.initPageNum - 1);
    }, [docReaderProps.initPageNum]);

    // 初始化
    useEffect(() => {
        const { resourceUrl, transStatus, pdfImages, initPageNum } = docReaderProps;
        imageLoadQueue = new ImageLoadQueue(pdfImages);
        lastPageNum = initPageNum;
        if (resourceUrl && transStatus !== ResourceStatus.WAIT_TRANSCODING) {
            setTimeout(
                () => {
                    if (renderType === RENDER_TYPE.IMAGE) {
                        initImage();
                    }
                },
                typeof $window.pdfjsLib === 'undefined' ? 2000 : 0,
            );
        }
    }, [docReaderProps.resourceUrl, docReaderProps.transStatus, docReaderProps.pdfImages, docReaderProps.initPageNum]);
    return isCurrentPageRendered;
}
